import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Package, ShoppingBag, Rocket, LayoutDashboard, FolderTree, RotateCcw, Users, Mail, Tag, Loader2, ShoppingCart, MailCheck, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SetupWizard from '../components/admin/SetupWizard';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminCategories from '../components/admin/AdminCategories';
import AdminReturns from '../components/admin/AdminReturns';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminNewsletter from '../components/admin/AdminNewsletter';
import AdminPromo from '../components/admin/AdminPromo';
import AdminAbandonedCarts from '../components/admin/AdminAbandonedCarts';
import AdminThanksEmail from '../components/admin/AdminThanksEmail';
import AdminMessages from '../components/admin/AdminMessages';
import AdminOrders from '../components/admin/AdminOrders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PRODUCT = {
  name: '',
  description: '',
  price: '',
  category: '',
  sizes: '',
  colors: '',
  images: '',
  stock: '',
  is_new: false,
  is_featured: false,
};

const AdminPage = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        const { data } = await axios.post(`${API}/admin/uploads/presign`, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          folder: (form.name || 'produit').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'produit',
        });
        // PUT direct vers S3 (URL presignée) — pas d'en-tête Authorization.
        await axios.put(data.upload_url, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          transformRequest: [(d) => d],
        });
        uploaded.push(data.public_url);
      }
      const existing = form.images.split(',').map((s) => s.trim()).filter(Boolean);
      setForm((f) => ({ ...f, images: [...existing, ...uploaded].join(', ') }));
      toast.success(`${uploaded.length} image(s) téléversée(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec du téléversement');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchProducts();
    }
  }, [isAdmin]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des produits');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_PRODUCT);
    setActiveTab('products');
    setIsFormOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      price: String(p.price),
      category: p.category,
      sizes: p.sizes.join(', '),
      colors: p.colors.join(', '),
      images: p.images.join(', '),
      stock: String(p.stock),
      is_new: p.is_new,
      is_featured: p.is_featured,
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_PRODUCT);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    price: parseFloat(form.price) || 0,
    category: form.category.trim(),
    sizes: form.sizes.split(',').map((s) => s.trim()).filter(Boolean),
    colors: form.colors.split(',').map((s) => s.trim()).filter(Boolean),
    images: form.images.split(',').map((s) => s.trim()).filter(Boolean),
    stock: parseInt(form.stock, 10) || 0,
    is_new: form.is_new,
    is_featured: form.is_featured,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price || !form.category.trim()) {
      toast.error('Nom, prix et catégorie sont obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, payload);
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Produit ajouté');
      }
      closeForm();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading || !isAdmin) return null;

  const inputClass =
    'w-full bg-transparent border border-black/20 py-2.5 px-3 focus:border-gold focus:outline-none text-foreground placeholder:text-foreground/40 transition-colors';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="py-12 border-b border-black/10 mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-widest text-gold mb-2">Espace Créateur</p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-heading text-4xl md:text-6xl tracking-tight"
            >
              Tableau de bord
            </motion.h1>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2 self-start" data-testid="admin-add-product-btn">
            <Plus size={18} />
            Ajouter un produit
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
            { id: 'products', label: `Produits (${products.length})`, icon: Package },
            { id: 'categories', label: 'Catégories', icon: FolderTree },
            { id: 'orders', label: 'Commandes', icon: ShoppingBag },
            { id: 'returns', label: 'Retours', icon: RotateCcw },
            { id: 'customers', label: 'Clients', icon: Users },
            { id: 'newsletter', label: 'Newsletter', icon: Mail },
            { id: 'promo', label: 'Codes promo', icon: Tag },
            { id: 'abandoned', label: 'Paniers abandonnés', icon: ShoppingCart },
            { id: 'messages', label: 'Messages', icon: MessageSquare },
            { id: 'thanks', label: 'Email remerciement', icon: MailCheck },
            { id: 'setup', label: 'Lancement', icon: Rocket },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest whitespace-nowrap transition-colors ${
                activeTab === t.id ? 'bg-gold text-black' : 'bg-black/5 text-foreground/70 hover:bg-black/10'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'dashboard' && <AdminDashboard />}
        {activeTab === 'categories' && <AdminCategories />}
        {activeTab === 'returns' && <AdminReturns />}
        {activeTab === 'customers' && <AdminCustomers />}
        {activeTab === 'newsletter' && <AdminNewsletter />}
        {activeTab === 'promo' && <AdminPromo />}
        {activeTab === 'abandoned' && <AdminAbandonedCarts />}
        {activeTab === 'messages' && <AdminMessages />}
        {activeTab === 'thanks' && <AdminThanksEmail />}
        {activeTab === 'orders' && <AdminOrders />}

        {/* Setup Wizard */}
        {activeTab === 'setup' && <SetupWizard />}

        {/* Products Table */}
        {activeTab === 'products' && (
          <div data-testid="admin-products-list">
            {products.length === 0 ? (
              <div className="text-center py-24 bg-white">
                <Package size={48} className="text-foreground/20 mx-auto mb-4" />
                <p className="text-foreground/60">Aucun produit. Ajoutez votre première pièce.</p>
              </div>
            ) : (
              <div className="border border-black/10 divide-y divide-white/10">
                {products.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-4 p-4 hover:bg-black/[0.03] transition-colors"
                    data-testid={`admin-product-row-${p.id}`}
                  >
                    <div className="w-16 h-20 bg-white overflow-hidden flex-shrink-0">
                      {p.images?.[0] && (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-lg truncate">{p.name}</p>
                      <p className="text-sm text-foreground/50">{p.category} · Stock: {p.stock}</p>
                      <div className="flex gap-2 mt-1">
                        {p.is_new && <span className="text-[10px] uppercase tracking-wider text-gold">Nouveau</span>}
                        {p.is_featured && <span className="text-[10px] uppercase tracking-wider text-foreground/50">Mis en avant</span>}
                      </div>
                    </div>
                    <p className="price-tag text-gold text-lg hidden sm:block">{p.price.toFixed(2)} €</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        className="p-2.5 border border-black/15 hover:border-gold hover:text-gold transition-colors"
                        data-testid={`admin-edit-${p.id}`}
                        aria-label="Modifier"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(p.id, p.name)}
                        className="p-2.5 border border-black/15 hover:border-red-500 hover:text-red-500 transition-colors"
                        data-testid={`admin-delete-${p.id}`}
                        aria-label="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Product Form Drawer */}
      <AnimatePresence>
        {isFormOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50"
              onClick={closeForm}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-lg bg-background z-50 overflow-y-auto border-l border-black/10"
              data-testid="admin-product-form"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-heading text-2xl">{editingId ? 'Modifier le produit' : 'Nouveau produit'}</h2>
                  <button onClick={closeForm} className="p-2 hover:text-gold transition-colors" data-testid="admin-close-form-btn">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Nom *</label>
                    <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Description</label>
                    <textarea rows={3} className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-form-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gold mb-2">Prix (€) *</label>
                      <input type="number" step="0.01" className={inputClass} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-form-price" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gold mb-2">Stock</label>
                      <input type="number" className={inputClass} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="admin-form-stock" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Catégorie *</label>
                    <input className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Robes, Vestes..." data-testid="admin-form-category" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Tailles (séparées par des virgules)</label>
                    <input className={inputClass} value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="XS, S, M, L, XL" data-testid="admin-form-sizes" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Couleurs (séparées par des virgules)</label>
                    <input className={inputClass} value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Noir/Or, Bordeaux" data-testid="admin-form-colors" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold mb-2">Images</label>
                    <textarea rows={2} className={inputClass} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="https://... (ou téléverse ci-dessous)" data-testid="admin-form-images" />
                    <label className="mt-3 inline-flex items-center gap-2 text-sm text-foreground/70 cursor-pointer hover:text-gold transition-colors">
                      <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="admin-image-upload" disabled={uploading} />
                      <span className="border border-black/20 px-4 py-2 inline-flex items-center gap-2">
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        {uploading ? 'Téléversement…' : 'Téléverser des images'}
                      </span>
                    </label>
                    {form.images && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {form.images.split(',').map((s) => s.trim()).filter(Boolean).map((url, i) => (
                          <img key={i} src={url} alt={`aperçu ${i + 1}`} className="w-16 h-16 object-cover border border-black/10" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-6 pt-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-gold" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} data-testid="admin-form-is-new" />
                      <span className="text-foreground/80 text-sm">Nouveauté</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-gold" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="admin-form-is-featured" />
                      <span className="text-foreground/80 text-sm">Mis en avant</span>
                    </label>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button type="button" onClick={closeForm} className="btn-secondary flex-1">Annuler</button>
                    <button type="submit" disabled={submitting} className="btn-primary flex-1 disabled:opacity-50" data-testid="admin-form-submit">
                      {submitting ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
